import React from "react";
import { motion } from "framer-motion";
import {
  TbBellRinging,
  TbDeviceMobile,
  TbChartBar,
  TbNetwork,
  TbBook2,
} from "react-icons/tb";
import { FaApple, FaGooglePlay } from "react-icons/fa";
import Badge from "../Badge/Badge";
import FeatureIcon from "../FeatureIcon/FeatureIcon";
import LazyImage from "../LazyImage/LazyImage";
import styles from "./MobileAppSection.module.css";

import { ifillip_mobile_app as mobileMockup } from "../../controller/Image";

const FEATURES = [
  {
    icon: TbDeviceMobile,
    title: "Access Across All Devices",
    text: "Seamlessly use the platform on mobile, tablet, or desktop anytime, anywhere without limitations.",
  },
  // {
  //   icon: TbNetwork,
  //   title: "Connected Smart School Ecosystem",
  //   text: "Bring students, teachers, and parents together in one unified platform for smooth collaboration.",
  // },
  // {
  //   icon: TbChartBar,
  //   title: "Learning Insights & Updates",
  //   text: "Track academic progress, performance trends, and key updates with clear, actionable insights.",
  // },
  {
    icon: TbBellRinging,
    title: "Smart Alerts & Notifications",
    text: "Receive real-time alerts for attendance, announcements, exams, and important school activities.",
  },
];

// Stagger animation config
const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

const MobileAppSection = () => {
  return (
    <section className={styles.section}>
      {/* Background ambient glow */}
      <div className={styles.bgGlow} aria-hidden="true" />

      <div className="page-container">
        <div className="row g-5 align-items-center">
          {/* ============ LEFT   CONTENT ============ */}
          <div className="col-12 col-lg-6">
            <motion.div
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.2 }}
              variants={containerVariants}
            >
              <motion.div variants={itemVariants}>
                <Badge variant="cyan">Mobile App Experience</Badge>
              </motion.div>

              <motion.h2 className={styles.title} variants={itemVariants}>
                <span className="gradientText">
                  Omnichannel Access Across All Devices
                </span>
              </motion.h2>

              <motion.p className={styles.description} variants={itemVariants}>
                Never miss an update wherever you are! iFillip provides a
                seamless experience across Web, Android, and iOS platforms
                enabling students, parents, teachers and administrators to
                access information, communicate and manage tasks from any
                device.
              </motion.p>

              {/* Feature list */}
              <motion.ul
                className={styles.featureList}
                variants={containerVariants}
              >
                {FEATURES.map((f) => (
                  <motion.li
                    key={f.title}
                    className={styles.featureItem}
                    variants={itemVariants}
                  >
                    <FeatureIcon icon={f.icon} tint="purple" size="sm" />
                    <div>
                      <h4 className={styles.featureTitle}>{f.title}</h4>
                      <p className={styles.featureText}>{f.text}</p>
                    </div>
                  </motion.li>
                ))}
              </motion.ul>

              {/* Download Section */}
              <motion.div
                className={styles.downloadWrap}
                variants={itemVariants}
              >
                <p className={styles.downloadEyebrow}>Get the iFillip app</p>
                <div className="row g-3">
                  <div className="col-6">
                    <a
                      target="blank"
                      href="https://play.google.com/store/apps/details?id=com.ifillip.school&pcampaignid=web_share"
                      className={styles.storeBtn}
                      aria-label="Download on Google Play"
                    >
                      <FaGooglePlay className={styles.storeIcon} />
                      <span className={styles.storeText}>
                        <span className={styles.storeSmall}>GET IT ON</span>
                        <span className={styles.storeBig}>Google Play</span>
                      </span>
                    </a>
                  </div>
                  <div className="col-6">
                    <a
                      target="blank"
                      href="https://apps.apple.com/in/app/ifillip/id6761409165"
                      className={styles.storeBtn}
                      aria-label="Download on the App Store"
                    >
                      <FaApple className={styles.storeIcon} />
                      <span className={styles.storeText}>
                        <span className={styles.storeSmall}>
                          Download on the
                        </span>
                        <span className={styles.storeBig}>App Store</span>
                      </span>
                    </a>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>

          {/* ============ RIGHT   VISUAL ============ */}
          <div className="col-12 col-lg-6">
            <motion.div
              className={styles.visualWrap}
              initial={{ opacity: 0, scale: 0.92 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.7, ease: "easeOut" }}
            >
              {/* Gradient orb behind phone */}
              <div className={styles.gradientOrb} aria-hidden="true" />

              {/* Floating phone */}
              <motion.div
                className={styles.phoneFloat}
                animate={{ y: [0, -14, 0] }}
                transition={{
                  duration: 5,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <LazyImage
                  {...mobileMockup}
                  alt="iFillip mobile app showing school management dashboard"
                  wrapperClassName={styles.phoneImg}
                />
              </motion.div>

              {/* Floating chips */}
              <motion.div
                className={`${styles.floatChip} ${styles.chipTop}`}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.5 }}
              >
                <TbBellRinging />
                <span>New attendance update</span>
              </motion.div>

              <motion.div
                className={`${styles.floatChip} ${styles.chipBottom}`}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.7 }}
              >
                <TbChartBar />
                <span>Term results published</span>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MobileAppSection;
